import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import { OverviewTab } from './tabs/OverviewTab';
import { PlansTab } from './tabs/PlansTab';
import { UsageTab } from './tabs/UsageTab';
import { PaymentsTab } from './tabs/PaymentsTab';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useSyncSubscription } from '../../api/hooks/useBilling';
import { track } from '../../utils/analytics';
import { Button } from '../../components/actions/Button';
import { TopHeader } from '../../components/navigation/TopHeader';

type TabId = 'overview' | 'plans' | 'usage' | 'payments';

interface Tab {
  id: TabId;
  label: string;
}

const tabs: Tab[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'plans', label: 'Plans' },
  { id: 'usage', label: 'Usage' },
  { id: 'payments', label: 'Payments' },
];

export function BillingLayout() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab') as TabId | null;
  const successParam = searchParams.get('success');
  const canceledParam = searchParams.get('canceled');
  const [activeTab, setActiveTab] = useState<TabId>(tabFromUrl || 'overview');
  const syncMutation = useSyncSubscription();
  const hasSynced = useRef(false);

  // Check if user is team owner or admin
  const currentTeam = user?.current_team;
  const userRole = currentTeam?.user_role;
  const isTeamAdmin = userRole === 'owner' || userRole === 'admin';

  // Sync with Stripe only after checkout success (not on every page load)
  useEffect(() => {
    if (successParam !== 'true' || hasSynced.current || !isTeamAdmin) return;
    hasSynced.current = true;

    const inviteId = searchParams.get('invite_id');

    // Sync subscription/payment from Stripe after checkout redirect
    syncMutation.mutate(inviteId ? { invite_id: parseInt(inviteId) } : undefined, {
      onSuccess: (data) => {
        if (data.invite_paid) {
          track('payment_completed', { invite_id: inviteId, type: 'per_invite' });
          showToast('Payment successful! Your invite is being published.', 'success');
        } else if (data.subscribed) {
          track('payment_completed', { type: 'planner' });
          showToast('Subscription activated successfully!', 'success');
        } else {
          showToast('Processing your payment...', 'info');
        }
      },
      onError: () => {
        showToast('Payment received. It may take a moment to activate.', 'info');
      },
    });

    // Clear success and invite_id params
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('success');
    newParams.delete('invite_id');
    setSearchParams(newParams, { replace: true });
  }, [successParam, syncMutation, showToast, searchParams, setSearchParams, isTeamAdmin]);

  // Handle checkout canceled
  useEffect(() => {
    if (canceledParam === 'true') {
      track('payment_abandoned');
      showToast('Checkout canceled', 'info');
      // Clear the canceled param
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('canceled');
      setSearchParams(newParams, { replace: true });
    }
  }, [canceledParam, showToast, searchParams, setSearchParams]);

  // Update URL when tab changes
  const handleTabChange = (tabId: TabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  // Sync state with URL changes (back/forward navigation)
  useEffect(() => {
    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl, activeTab]);

  // Show authorization error if user is not team admin/owner
  if (!isTeamAdmin) {
    return (
      <div className="min-h-screen flex flex-col">
        <TopHeader title="Billing" />
        <div className="flex-1 p-4 md:p-8">
          <div className="max-w-2xl mx-auto py-12">
            <div className="bg-background-card rounded-xl p-8 border border-border text-center">
              <div className="w-16 h-16 rounded-full bg-amber-500/10 mx-auto mb-4 flex items-center justify-center">
                <ShieldAlert className="w-8 h-8 text-amber-500" />
              </div>
              <h2 className="text-xl font-semibold text-text-primary mb-2">
                Access Restricted
              </h2>
              <p className="text-text-secondary mb-6">
                Only team owners and admins can access billing settings. Please contact your team owner if you need to manage billing.
              </p>
              <Button onClick={() => navigate(-1)}>
                Go Back
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <TopHeader title="Billing" />

      {/* Tab Navigation */}
      <div className="border-b border-border bg-background-card">
        <nav className="flex gap-1 md:gap-2 -mb-px overflow-x-auto px-4 md:px-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`
                px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap
                ${activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-text-secondary hover:text-text-primary hover:border-border'
                }
              `}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="flex-1 p-4 md:p-8">
        {activeTab === 'overview' && <OverviewTab />}
        {activeTab === 'plans' && <PlansTab />}
        {activeTab === 'usage' && <UsageTab />}
        {activeTab === 'payments' && <PaymentsTab />}
      </div>
    </div>
  );
}
